function nonEmpty(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Placement for one host field. An explicit step id wins. A global
 * control stays global even when the page is sitting on a section.
 * Otherwise the step id is the section slug, or the section and
 * sub-tab slugs the host already placed on the page. The step name
 * is the sub-tab label, then an authored name, then the section
 * label. A filter with no section is Global. The step name is never
 * the field id or field label.
 */
export function resolveHostFieldPlacement(
  tracking: Readonly<Record<string, unknown>> | undefined,
  page: Readonly<Record<string, unknown>> | undefined,
): { readonly stepId: string; readonly stepName: string } {
  const explicitName =
    nonEmpty(tracking?.stepName) ?? nonEmpty(page?.stepName) ?? '';
  const explicitId = nonEmpty(tracking?.stepId);
  if (explicitId) return { stepId: explicitId, stepName: explicitName };
  if (tracking?.placement === 'global' || page?.placement === 'global') {
    return { stepId: 'global', stepName: 'Global' };
  }
  const section = nonEmpty(page?.sectionSlug);
  const subTab = nonEmpty(page?.subTabSlug);
  const sectionLabel = nonEmpty(page?.sectionLabel);
  const subTabLabel = nonEmpty(page?.subTabLabel);
  if (section && subTab) {
    return {
      stepId: `${section}#${subTab}`,
      stepName: subTabLabel ?? nonEmpty(explicitName) ?? sectionLabel ?? '',
    };
  }
  if (section) {
    return { stepId: section, stepName: sectionLabel ?? explicitName };
  }
  return { stepId: 'global', stepName: 'Global' };
}

/**
 * Host field values are strings on the wire. A multi-select binding
 * may still hold a string array in form state; view and input both
 * join that array before sanitizing.
 */
export function normalizeHostFieldValue(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .join(',');
}

export function hasCommittedHostFieldValue(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) {
    return value.some(
      (entry) => typeof entry === 'string' && entry.trim().length > 0,
    );
  }
  return false;
}

/**
 * Shared FieldViewed / FieldInputted identity. Both events include
 * `fieldValue`. On the first view that value is the default already
 * in form state.
 */
export function buildHostFieldEventContext(input: {
  readonly page?: Readonly<Record<string, unknown>>;
  readonly tracking?: Readonly<Record<string, unknown>>;
  readonly formKey?: string;
  readonly nodeId?: string;
  readonly label?: string;
  readonly fieldValue?: string;
}): Record<string, unknown> {
  const tracking = input.tracking;
  const placement = resolveHostFieldPlacement(tracking, input.page);
  return {
    fieldName: nonEmpty(tracking?.fieldName) ?? nonEmpty(input.formKey) ?? '',
    fieldType: nonEmpty(tracking?.fieldType) ?? '',
    fieldLabel: nonEmpty(tracking?.fieldLabel) ?? nonEmpty(input.label) ?? '',
    fieldId: nonEmpty(tracking?.fieldId) ?? nonEmpty(input.nodeId) ?? '',
    stepId: placement.stepId,
    stepName: placement.stepName,
    fieldValue: input.fieldValue ?? '',
  };
}
